import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, MessageCircle, CreditCard, Truck, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { contactInfo } from '../data/mock';

const PurchaseModal = ({ product, isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    paymentMethod: ''
  });

  const whatsappNumber = contactInfo.social.whatsapp;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWhatsAppPurchase = () => {
    const price = product.discountPrice || product.price;
    const message = encodeURIComponent(
      `¡Hola! Quiero comprar:\n\n` +
      `📦 Producto: ${product.name}\n` +
      `💰 Precio: ${formatPrice(price)}\n\n` +
      `👤 Nombre: ${formData.name}\n` +
      `📱 Teléfono: ${formData.phone}\n` +
      `📧 Email: ${formData.email || 'No especificado'}\n` +
      `📍 Dirección: ${formData.address || 'Retiro en local'}\n` +
      `💳 Forma de pago: ${formData.paymentMethod || 'A coordinar'}\n\n` +
      `¡Gracias!`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
    setStep(4);
  };

  const resetAndClose = () => {
    setStep(1);
    setFormData({ name: '', phone: '', email: '', address: '', paymentMethod: '' });
    onClose();
  };

  if (!product) return null;

  const price = product.discountPrice || product.price;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={resetAndClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-gray-900 border border-violet-500/30 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-violet-500/20">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-violet-400" />
                Realizar Compra
              </h2>
              <button
                onClick={resetAndClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 p-4 border-b border-violet-500/10">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                    ${step >= s ? 'bg-violet-600 text-white' : 'bg-gray-700 text-gray-400'}`}>
                    {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-12 h-1 mx-1 rounded transition-colors
                      ${step > s ? 'bg-violet-600' : 'bg-gray-700'}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Content */}
            <div className="p-5">
              {/* Step 1: Product Info */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-4"
                >
                  <div className="bg-violet-600/10 border border-violet-500/20 rounded-xl p-4">
                    <h3 className="text-white font-semibold text-lg mb-2">{product.name}</h3>
                    <p className="text-gray-400 text-sm mb-3">{product.description}</p>
                    <div className="flex items-baseline gap-3">
                      <span className="text-2xl font-bold text-violet-400">{formatPrice(price)}</span>
                      {product.originalPrice && (
                        <span className="text-gray-500 line-through text-sm">
                          {formatPrice(product.originalPrice)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => setStep(2)}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full py-6 font-semibold"
                  >
                    Continuar
                  </Button>
                </motion.div>
              )}

              {/* Step 2: Contact Info */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-4"
                >
                  <h3 className="text-white font-semibold mb-4">Tus datos de contacto</h3>
                  
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Nombre completo *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Tu nombre"
                      className="bg-black/50 border-violet-900/50 text-white placeholder:text-gray-600"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Teléfono / WhatsApp *</label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Tu número de teléfono"
                      className="bg-black/50 border-violet-900/50 text-white placeholder:text-gray-600"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Email (opcional)</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="tu@email.com"
                      className="bg-black/50 border-violet-900/50 text-white placeholder:text-gray-600"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Dirección de entrega (opcional)</label>
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Dejar vacío para retiro en local"
                      className="bg-black/50 border-violet-900/50 text-white placeholder:text-gray-600"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="flex-1 border-violet-500/50 text-violet-300 hover:bg-violet-600/20 rounded-full py-6"
                    >
                      Atrás
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      disabled={!formData.name || !formData.phone}
                      className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full py-6 disabled:opacity-50"
                    >
                      Continuar
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Payment Method */}
              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-4"
                >
                  <h3 className="text-white font-semibold mb-4">Forma de pago</h3>
                  
                  <div className="space-y-3">
                    {['Efectivo', 'Transferencia bancaria', 'Tarjeta de débito', 'Tarjeta de crédito', 'MercadoPago'].map((method) => (
                      <button
                        key={method}
                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: method }))}
                        className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-3
                          ${formData.paymentMethod === method 
                            ? 'bg-violet-600/20 border-violet-500 text-white' 
                            : 'bg-black/30 border-violet-900/30 text-gray-400 hover:border-violet-500/50'}`}
                      >
                        <CreditCard className="w-5 h-5" />
                        {method}
                      </button>
                    ))}
                  </div>
                  
                  {/* Summary */}
                  <div className="bg-violet-600/10 border border-violet-500/20 rounded-xl p-4 mt-4">
                    <h4 className="text-white font-semibold mb-2">Resumen</h4>
                    <div className="text-sm space-y-1">
                      <p className="text-gray-400">Producto: <span className="text-white">{product.name}</span></p>
                      <p className="text-gray-400">Total: <span className="text-violet-400 font-bold">{formatPrice(price)}</span></p>
                      <p className="text-gray-400">Entrega: <span className="text-white">{formData.address || 'Retiro en local'}</span></p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1 border-violet-500/50 text-violet-300 hover:bg-violet-600/20 rounded-full py-6"
                    >
                      Atrás
                    </Button>
                    <Button
                      onClick={handleWhatsAppPurchase}
                      disabled={!formData.paymentMethod}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-full py-6 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Enviar por WhatsApp
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Success */}
              {step === 4 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">¡Pedido enviado!</h3>
                  <p className="text-gray-400 mb-6">
                    Tu solicitud fue enviada por WhatsApp. Te responderemos a la brevedad para coordinar la compra.
                  </p>
                  <Button
                    onClick={resetAndClose}
                    className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 py-6"
                  >
                    Cerrar
                  </Button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PurchaseModal;
